import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Grid, 
  Divider, 
  Chip, 
  LinearProgress, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FavoriteIcon from '@mui/icons-material/Favorite';
import OpacityIcon from '@mui/icons-material/Opacity';
import DeviceThermostatIcon from '@mui/icons-material/DeviceThermostat';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { PhysiologicalStatus, WearableRiskData, PhysiologicalRiskScores } from '../../types/wearable';

interface PhysiologicalMonitorProps {
  wearableId?: string;
  physiologicalStatus?: PhysiologicalStatus;
  wearableRiskData?: WearableRiskData;
  memberName?: string;
}

const PhysiologicalMonitor: React.FC<PhysiologicalMonitorProps> = ({
  wearableId,
  physiologicalStatus,
  wearableRiskData,
  memberName
}) => {
  const [showDetails, setShowDetails] = useState(false);
  const [status, setStatus] = useState<PhysiologicalStatus | undefined>(physiologicalStatus);
  const [riskScores, setRiskScores] = useState<PhysiologicalRiskScores | undefined>(
    wearableRiskData?.risk_scores || physiologicalStatus?.risk_scores
  );

  // props 변경 시 상태 업데이트
  useEffect(() => {
    setStatus(physiologicalStatus);
    setRiskScores(wearableRiskData?.risk_scores || physiologicalStatus?.risk_scores);
  }, [physiologicalStatus, wearableRiskData]);

  // 데이터가 없는 경우
  if (!status || !status.available) {
    return (
      <Card sx={{ minWidth: 300, mb: 2, bgcolor: '#f5f5f5' }}>
        <CardContent>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            생체 신호 모니터링 {memberName ? `- ${memberName}` : ''}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 100 }}>
            <Typography variant="body1" color="text.secondary">
              {status?.message || '생체 데이터 없음'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // 위험도 색상 매핑 함수
  const getRiskColor = (score: number) => {
    if (score >= 7) return '#f44336'; // 고위험
    if (score >= 4) return '#ff9800'; // 중위험
    return '#4caf50'; // 저위험
  };

  // 위험도 레벨 텍스트 반환 함수
  const getRiskLevelText = (score: number) => {
    if (score >= 7) return '고위험';
    if (score >= 4) return '중위험';
    return '안전';
  };

  return (
    <Card sx={{ 
      minWidth: 300, 
      mb: 2, 
      bgcolor: status.risk_status === '위험' || status.risk_status === 'risk' ? '#fff8f8' : '#f8fffa',
      border: status.risk_status === '위험' || status.risk_status === 'risk' ? '1px solid #ffcccc' : '1px solid #ccffcc'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <HealthAndSafetyIcon sx={{ mr: 1, color: status.risk_status === '위험' || status.risk_status === 'risk' ? '#f44336' : '#4caf50' }} />
            생체 신호 모니터링 {memberName ? `- ${memberName}` : ''}
          </Typography>
          {(status.risk_status === '위험' || status.risk_status === 'risk') && (
            <Chip 
              icon={<WarningIcon />} 
              label="위험 상태" 
              color="error" 
              variant="outlined" 
              size="small"
            />
          )}
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={2}>
          {/* 심박수 정보 */}
          {status.heart_rate && (
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FavoriteIcon sx={{ mr: 1, color: '#f44336' }} />
                  <Typography variant="body1">심박수</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {status.heart_rate.current} <Typography component="span" variant="body2">BPM</Typography>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption">최소: {status.heart_rate.min}</Typography>
                  <Typography variant="caption">평균: {status.heart_rate.avg.toFixed(1)}</Typography>
                  <Typography variant="caption">최대: {status.heart_rate.max}</Typography>
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* 산소포화도 정보 */}
          {status.spo2 && (
            <Grid item xs={12} sm={6}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <OpacityIcon sx={{ mr: 1, color: '#2196f3' }} />
                  <Typography variant="body1">산소포화도</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {status.spo2.current}% <Typography component="span" variant="body2">SpO2</Typography>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption">최소: {status.spo2.min}%</Typography>
                  <Typography variant="caption">평균: {status.spo2.avg.toFixed(1)}%</Typography>
                </Box>
              </Paper>
            </Grid>
          )}
          
          {/* 혈압 정보 */}
          {status.blood_pressure && (
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 1, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DeviceThermostatIcon sx={{ mr: 1, color: '#673ab7' }} />
                  <Typography variant="body1">혈압</Typography>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                  {status.blood_pressure.current} <Typography component="span" variant="body2">mmHg</Typography>
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption">평균 수축기: {status.blood_pressure.systolic_avg.toFixed(0)}</Typography>
                  <Typography variant="caption">평균 이완기: {status.blood_pressure.diastolic_avg.toFixed(0)}</Typography>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
        
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2">위험도 분석</Typography>
            <IconButton 
              aria-label="details" 
              size="small" 
              onClick={() => setShowDetails(!showDetails)}
            >
              {showDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
          
          {/* 종합 위험도 점수 */}
          {riskScores && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2">종합 위험도</Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 'bold', 
                    color: getRiskColor(riskScores.overall)
                  }}
                >
                  {getRiskLevelText(riskScores.overall)} ({riskScores.overall.toFixed(1)}/10)
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={riskScores.overall * 10} 
                sx={{ 
                  mt: 0.5, 
                  mb: 1, 
                  height: 10, 
                  borderRadius: 5,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: getRiskColor(riskScores.overall)
                  }
                }} 
              />
            </Box>
          )}
          
          {/* 세부 위험도 정보 */}
          <Collapse in={showDetails}>
            {riskScores && (
              <TableContainer component={Paper} elevation={0} sx={{ mt: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>위험 유형</TableCell>
                      <TableCell align="right">점수</TableCell>
                      <TableCell align="right">위험 수준</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">심장 위험도</TableCell>
                      <TableCell align="right">{riskScores.cardiac.toFixed(1)}</TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: getRiskColor(riskScores.cardiac),
                          fontWeight: 'bold'
                        }}
                      >
                        {getRiskLevelText(riskScores.cardiac)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">호흡 위험도</TableCell>
                      <TableCell align="right">{riskScores.respiratory.toFixed(1)}</TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: getRiskColor(riskScores.respiratory),
                          fontWeight: 'bold'
                        }}
                      >
                        {getRiskLevelText(riskScores.respiratory)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell component="th" scope="row">부정맥 위험도</TableCell>
                      <TableCell align="right">{riskScores.dysrhythmia.toFixed(1)}</TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          color: getRiskColor(riskScores.dysrhythmia),
                          fontWeight: 'bold'
                        }}
                      >
                        {getRiskLevelText(riskScores.dysrhythmia)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Collapse>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PhysiologicalMonitor; 